import { useCallback, useEffect, useState } from "react";

import { api } from "../api";

type Note = {
  ts?: number;
  text?: string;
  version?: string;
  userId?: string;
  anonId?: string;
};

/**
 * What readers have written. Its own page because it is the one thing in the
 * events stream a person typed, and it should not have to be hunted for among
 * a thousand card views. Kept when usage events are cleared.
 */
export function Feedback() {
  const [notes, setNotes] = useState<Note[]>();
  const [err, setErr] = useState("");
  const [clearing, setClearing] = useState(false);
  const [cleared, setCleared] = useState("");

  const load = useCallback(() => {
    api<{ feedback: Note[] }>("/admin/feedback?limit=200")
      .then((r) => setNotes(r.feedback))
      .catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  async function clearEvents() {
    if (!confirm("Delete every usage event? Accounts, reading history and feedback are kept. This cannot be undone.")) return;
    setClearing(true);
    try {
      const r = await api<{ removed: number }>("/admin/events/clear", { method: "POST", body: { days: 90 } });
      setCleared(`${r.removed} events deleted.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setClearing(false);
    }
  }

  if (err) return <div className="msg err">{err}</div>;
  if (!notes) return <p className="chrome">Loading…</p>;
  return (
    <>
      <h2>Feedback</h2>
      <p className="sub">
        What readers wrote in the app, newest first. Kept indefinitely, and kept when usage events are cleared.
      </p>

      {notes.length === 0 ? (
        <p className="chrome">Nothing yet.</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr><th>When</th><th>Version</th><th>Who</th><th>What they said</th></tr>
          </thead>
          <tbody>
            {notes.map((n, i) => (
              <tr key={i}>
                <td className="chrome">{n.ts ? new Date(Number(n.ts)).toLocaleString() : ""}</td>
                <td className="chrome">{n.version ?? ""}</td>
                <td className="chrome">
                  {n.userId ? `user ${String(n.userId).slice(0, 8)}…` : `anon ${String(n.anonId ?? "").slice(0, 8)}`}
                </td>
                <td style={{ whiteSpace: "pre-wrap" }}>{n.text ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 style={{ marginTop: 40 }}>Start a round from zero</h2>
      <p className="sub">
        Deletes every usage event so a testing round can be read on its own. Accounts, reading history,
        streaks, saved cards and the feedback above are untouched.
      </p>
      {cleared && <div className="msg">{cleared}</div>}
      <button onClick={clearEvents} disabled={clearing}>
        {clearing ? "Deleting…" : "Delete all usage events"}
      </button>
    </>
  );
}
